/**
 * Agentic Workflows Handlers
 *
 * Contains the main handler functions for:
 * - Deep Research (multi-stage Perplexity research)
 * - Report Formatting (Claude-powered formatting)
 * - Spanish Translation (Claude-powered translation)
 */

import { getFromLocalStorage } from '../../components/GlobalChatSystem/utils';
import { GLOBAL_CHAT_HISTORY_KEY } from '../../components/GlobalChatSystem/types';
import { getPendingWidgetHtml } from '../globalTools/widgetHtml';
import { SPECIAL_MESSAGE_COLORS } from '../../components/GlobalChatSystem/styles';
import type { TokenUsageMetadata, SystemMessageInput, ChatMessage } from '../../components/GlobalChatSystem/types';

import { runPerplexityResearch, runPerplexityResearchStream, PERPLEXITY_PROXY_URL_DIRECT } from '../perplexityClient';
import { runVideoScriptAnalysis, runVideoScriptGeneration, formatVideoScriptError } from '../videoScriptService';
import { BUTTON_IDS as GLOBAL_VIDEO_SCRIPT_BUTTON_IDS } from '../globalTools/tools/videoScript';
import {
  runTranslation,
  formatTranslationError,
  buildTranslationManifest,
  formatManifestForTranslation,
  parseTranslatedManifest,
  extractBodyContentFromPartialTranslation,
} from '../translator';
import { analyzeDocumentsWithClaude, hasAnalyzableFiles } from '../claudeDocumentAnalyzer';
import { generateDualFormatDocumentsWithStorage, generateFormattedDocuments } from '../../utils/documentGenerator';
import { fetchCurrentTenantBranding } from '../tenantBranding';
import { getAuth } from 'firebase/auth';
import type { SerializedAttachment } from '../../components/GlobalChatSystem/types';
import { serializeAttachmentWithStorage } from '../../utils/attachmentStorage';
import { buildVideoScriptPdf } from '../../utils/videoScriptPdf';
import {
  PERPLEXITY_DEEP_RESEARCH_MODEL,
  PERPLEXITY_DEEP_RESEARCH_DISPLAY,
  PERPLEXITY_DEEP_RESEARCH_MAX_TOKENS,
  PERPLEXITY_DEEP_RESEARCH_STAGE1_MODEL,
  PERPLEXITY_DEEP_RESEARCH_STAGE1_DISPLAY,
  PERPLEXITY_DEEP_RESEARCH_STAGE2_MODEL,
  PERPLEXITY_DEEP_RESEARCH_STAGE2_DISPLAY,
  PERPLEXITY_DEEP_RESEARCH_STAGES_1_2_REASONING_EFFORT,
  PERPLEXITY_REASONING_EFFORT,
  PERPLEXITY_SEARCH_CONTEXT_SIZE,
  CLAUDE_DOCUMENT_ANALYSIS_DISPLAY,
  CLAUDE_TRANSLATOR_DISPLAY,
} from '../../config/agenticWorkflowConfig';
import { hasKnownPricing } from '../../config/aiModelPricing';

import {
  CANCEL_KEYWORDS,
  TRANSLATE_SPANISH_PREFIX,
  CHAT_TRANSLATE_BUTTON_ID,
  CHAT_VIDEO_SCRIPT_TRANSLATE_BUTTON_ID,
  PERPLEXITY_VIDEO_SCRIPT_STAGE1_DISPLAY,
  CLAUDE_VIDEO_SCRIPT_GENERATION_DISPLAY,
  STAGE1_SYSTEM_PROMPT,
  STAGE2_SYSTEM_PROMPT,
  buildDeepResearchSystemPrompt,
} from './constants';
import { INITIAL_DR_SESSION } from './types';
import type { AgenticWorkflowsHook } from './index';
import {
  stripThinkingBlocks,
  generateConciseTitle,
  parseDeepResearchPrompt,
  parseVideoScriptTrigger,
  isCancelCommand,
  parseStage1Json,
  formatStage1InitialFindings,
  formatResearchToHtml,
  formatPerplexityError,
  parseVideoScriptStage1Response,
  formatVideoScriptClarifyingQuestionsHtml,
  parseVideoScriptStage2Response,
  formatVideoScriptOutputHtml,
  VIDEO_SCRIPT_COLORS,
  generateSpecialMessageTemplate,
  extractLatestWorkflowOutput,
  generateWorkflowActionButtonsHtml,
  generateQueryBasedInitialContext,
  ensureReferencesSection,
  appendMissingCitationsToReferences,
  buildDeepResearchQuickAnswerCtaHtml,
  PREST0_PRIMARY_CTA_BG,
  PREST0_SECONDARY_CTA_BG,
} from './helpers';
import type { VideoScriptSourceContent, VideoScriptClarification } from './types';
import { detectInputLanguage, buildLanguageDirective, getDeepResearchChromeStrings } from './languageMirror';

// ============================================================================
// Model Display Name Helper
// ============================================================================

// 2026-05-23: Avatar/Platform Bridge event emissions for deep-research
// and related workflows. Each emission is a safe no-op when no avatar
// iframe is registered. See AVATAR_BRIDGE_PROTOCOL.md for taxonomy.
import { broadcastBridgeEvent } from './broadcastBridgeEvent';

// Model display names come from the single consolidated builder (2026-07-06:
// this file's local copy had drifted — raw-vendor-id fallthrough, unversioned
// Opus pinned to Apex-4.7 instead of 4.8, no Nova-5 branch).
import { buildModelDisplay } from '../globalTools/modelDisplay';

/**
 * Turn a scanned-document read into a sentence the user can watch move.
 *
 * WHY (2026-08-21): reading a 30-page scan renders every page and then makes a
 * vision call per batch of four — minutes of work behind a spinner that said
 * only "reading attachment". Miles watched it for 8 minutes with no way to
 * tell working from hung, and neither could I. Say where it is.
 */
function readingAttachmentLabel(info: { phase: string; done: number; total: number }): string {
  if (!info?.total) return 'reading attachment';
  return info.phase === 'rendering'
    ? `reading attachment · page ${info.done} of ${info.total}`
    : `transcribing the scan · page ${info.done} of ${info.total}`;
}


// ============================================================================
// Custom Theme Constants for Special Messages
// ============================================================================

/** Purple theme for formatting/translation complete messages */
const PURPLE_THEME = {
  accent: '#7C3AED',
  accentBg: '#EDE9FE',
  border: '#a78bfa',
  subtitle: '#475569',
  muted: '#64748b',
};

/** Green theme for attachment read success messages */
const GREEN_ATTACHMENT_THEME = {
  accent: '#065f46',
  accentBg: '#d1fae5',
  border: '#10b981',
  subtitle: '#475569',
  muted: '#64748b',
};

/** Amber theme for warning messages */
const AMBER_THEME = {
  accent: '#d97706',
  accentBg: '#fef3c7',
  border: '#fbbf24',
  subtitle: '#475569',
  muted: '#64748b',
};

/** Red theme for error messages */
const RED_ERROR_THEME = {
  accent: '#dc2626',
  accentBg: '#fef2f2',
  border: '#fca5a5',
  subtitle: '#7f1d1d',
  muted: '#991b1b',
};

// ============================================================================
// Handler Interfaces
// ============================================================================

interface StreamingMessageHelpers {
  startStreamingMessage: (initial: SystemMessageInput) => { index: number; id: string };
  updateStreamingMessage: (index: number, update: Partial<ChatMessage>) => void;
  updateStreamingMessageById?: (id: string, update: Partial<ChatMessage>) => void;
  finalizeStreamingMessage: (index: number, final: Partial<ChatMessage>) => void;
  finalizeStreamingMessageById?: (id: string, final: Partial<ChatMessage>) => void;
  removeMessage: (index: number) => void;
  removeMessageById?: (id: string) => void;
}

interface WorkflowHelpersInterface {
  streaming: StreamingMessageHelpers;
  addSystemMessage: (input: SystemMessageInput) => ChatMessage;
  addMessage: (message: ChatMessage) => ChatMessage;
}

// ============================================================================
// Translate Handler
// ============================================================================

/**
 * Translate the latest content (research or formatted report) to Spanish.
 *
 * @deprecated Use translateTool via GlobalTools instead.
 * This function is kept for backwards compatibility only.
 * All new code should use GlobalTools.handleButtonClick or message triggers.
 * The unified translation tool is in src/services/globalTools/tools/translate.ts
 */
export async function handleTranslateToSpanish(
  helpers: WorkflowHelpersInterface,
  workflows: AgenticWorkflowsHook
): Promise<boolean> {
  // Use ref to get latest translatable content
  let content = workflows.lastTranslatableContentRef.current || workflows.lastTranslatableContent;

  // Fallback: Try to recover content from persisted chat history
  if (!content) {
    const history = getFromLocalStorage<ChatMessage[]>(GLOBAL_CHAT_HISTORY_KEY, []);
    // Try formatted content first, then research report
    const formatted = extractLatestWorkflowOutput(history, 'formatted_content');
    const research = extractLatestWorkflowOutput(history, 'research_report');
    const historyOutput = formatted || research;

    if (historyOutput) {
      content = {
        content: historyOutput.content,
        title: historyOutput.title || 'Content',
        timestamp: new Date(historyOutput.timestamp),
        source: historyOutput.type === 'formatted_content' ? 'formatted' : 'research',
      };
      // Update refs for future use
      workflows.setLastTranslatableContent(content);
      console.log('[AgenticWorkflows] Recovered content from chat history for translation');
    }
  }

  // Check if there's content to translate
  if (!content) {
    helpers.addSystemMessage({
      text: 'no content available to translate. run deep research or format a report first, then click "translate to spanish" below or type "translate to spanish" in chat.',
      html: generateSpecialMessageTemplate({
        header: { title: 'no content available' },
        sections: [
          {
            type: 'text',
            content: 'run deep research or format a report first, then click <strong>translate to spanish</strong> in this chat or type <strong>"translate to spanish"</strong>.',
            style: { fontSize: '0.9rem', custom: 'line-height:1.5;' },
          },
        ],
        theme: 'custom',
        customColors: PURPLE_THEME,
      }),
      sourceTag: 'translation_status',
    });
    return true;
  }

  if (workflows.isTranslating) {
    helpers.addSystemMessage({
      text: 'translation is already in progress. please wait for it to complete.',
      html: `<div style="display:flex;flex-direction:column;gap:8px;margin:4px 0;">
        <strong style="color:#7C3AED;font-size:0.95rem;">translation in progress</strong>
        <span style="color:#475569;font-size:0.9rem;line-height:1.5;">please wait for the current translation to complete.</span>
      </div>`,
      sourceTag: 'translation_status',
    });
    return true;
  }

  workflows.setIsTranslating(true);

  // Start a streaming message for the translation
  const { index: translationStreamingIndex, id: translationStreamingId } = helpers.streaming.startStreamingMessage({
    text: 'translating to spanish…',
    html: getPendingWidgetHtml('translating to spanish'),
    sourceTag: 'translation_streaming',
  });

  // Allow React to render the pending widget before starting async work
  await new Promise((resolve) => setTimeout(resolve, 0));

  try {
    let translatedText = '';
    let translationTokenUsage: TokenUsageMetadata | undefined;

    // Build translation manifest with the active tenant's firm name so
    // translated PDF/DOCX metadata does not inherit Fernando branding for
    // T1/T2 tenants (MT-QA-BUG-001).
    const tenantBrandingForManifest = await fetchCurrentTenantBranding();
    const manifest = buildTranslationManifest({
      title: content.title,
      content: content.content,
      createdAt: content.timestamp,
      source: content.source,
      firmName: tenantBrandingForManifest.firmName,
    });

    const manifestText = formatManifestForTranslation(manifest);

    await runTranslation({
      documentContent: manifestText,
      targetLanguage: 'Spanish',
      onChunk: (_chunk: string, accumulated: string) => {
        const bodyPreview = extractBodyContentFromPartialTranslation(accumulated);
        const updatePayload = {
          text: bodyPreview || 'translating document metadata…',
          html: bodyPreview
            ? formatResearchToHtml(bodyPreview)
            : getPendingWidgetHtml('translating document metadata'),
          sourceTag: 'translation_streaming',
        };
        // Prefer ID-based update
        if (helpers.streaming.updateStreamingMessageById) {
          helpers.streaming.updateStreamingMessageById(translationStreamingId, updatePayload);
        } else {
          helpers.streaming.updateStreamingMessage(translationStreamingIndex, updatePayload);
        }
      },
      onComplete: (fullText: string, tokenUsage?: TokenUsageMetadata) => {
        translatedText = fullText;
        translationTokenUsage = tokenUsage;
        if (tokenUsage) {
          workflows.recordTransaction({
            tokenUsage,
            source: 'agenticWorkflows',
            modelOverride: buildModelDisplay(tokenUsage, 'Translation', CLAUDE_TRANSLATOR_DISPLAY),
            summary: 'spanish translation',
          });
        }
      },
      onError: (err: Error) => {
        const errorPayload = {
          text: `Translation error: ${err.message}`,
          html: `<div style="color:#dc2626;font-weight:600;">Translation error: ${err.message}</div>`,
          sourceTag: 'translation_error',
        };
        // Prefer ID-based update
        if (helpers.streaming.updateStreamingMessageById) {
          helpers.streaming.updateStreamingMessageById(translationStreamingId, errorPayload);
        } else {
          helpers.streaming.updateStreamingMessage(translationStreamingIndex, errorPayload);
        }
      },
    });

    // Parse the translated manifest
    const translated = parseTranslatedManifest(translatedText);

    // Build translated PDF and DOCX
    const { pdf: translatedPdf, docx: translatedDocx, pdfSerialized: translatedPdfSerialized, docxSerialized: translatedDocxSerialized } = await generateFormattedDocuments({
      content: translated.bodyContent,
      title: content.title,
      createdAt: content.timestamp,
      translations: {
        title: translated.title,
        date: translated.date,
        organizationName: translated.firmName,
        copyrightNotice: translated.copyrightNotice,
        copyrightShort: translated.copyrightShort,
        pageNumberFormat: translated.pageNumberFormat,
      },
    });

    // Finalize the streaming message
    const finalTranslatedHtml = formatResearchToHtml(translated.bodyContent);
    const finalPayload = {
      text: translated.bodyContent,
      html: finalTranslatedHtml,
      sourceTag: 'translation_result',
      attachments: [translatedPdf, translatedDocx],
      attachmentsSerialized: [translatedPdfSerialized, translatedDocxSerialized],
      tokenUsage: translationTokenUsage ? { ...translationTokenUsage, summary: 'spanish translation' } : undefined,
    };
    // Prefer ID-based finalize
    if (helpers.streaming.finalizeStreamingMessageById) {
      helpers.streaming.finalizeStreamingMessageById(translationStreamingId, finalPayload);
    } else {
      helpers.streaming.finalizeStreamingMessage(translationStreamingIndex, finalPayload);
    }

    // Set translatable content with the translated Spanish content for video script workflow
    const translatedTranslatableData = {
      content: translated.bodyContent,
      title: translated.title || content.title,
      timestamp: new Date(),
      source: 'formatted' as const,
    };
    workflows.setLastTranslatableContent(translatedTranslatableData);
    workflows.lastTranslatableContentRef.current = translatedTranslatableData;

    // ONE MESSAGE PER DELIVERABLE (owner redundancy directive 2026-08-29):
    // the finalized translation above IS the completion — the extra
    // "spanish translation complete" tile restated it, and the modern
    // translate lane already ships buttonless translation results
    // (2026-08-22 owner directive). Ask-in-words reaches the video-script
    // workflow exactly as it does on the modern lane.
  } catch (error) {
    console.error('translation failed', error);
    helpers.addSystemMessage({
      text: `unable to translate content: ${formatTranslationError(error)}`,
      sourceTag: 'translation_error',
    });
  } finally {
    workflows.setIsTranslating(false);
  }

  return true;
}

// ============================================================================
// Deep Research Handler
// ============================================================================

/**
 * Handle deep research workflow.
 * This is a multi-stage process:
 * 1. Qualification (determine if deep research is needed)
 * 2. Strategy (build research brief)
 * 3. Execution (run deep research with streaming)
 */
export async function handleDeepResearch(
  message: ChatMessage,
  helpers: WorkflowHelpersInterface,
  workflows: AgenticWorkflowsHook
): Promise<boolean> {
  const text = (message.text || '').trim();

  // Store helpers in ref for event delegation
  workflows.chatHelpersRef.current = helpers;

  // NOTE: Translation commands now route through GlobalTools translateTool
  // for unified handling. See translate.ts for the implementation.

  // IMPORTANT: Check for video script clarification input BEFORE checking for new triggers
  // This prevents user replies in stage1_clarify from accidentally re-triggering Stage 1 analysis
  const videoSession = workflows.videoScriptSessionRef?.current || workflows.videoScriptSession;
  if (videoSession?.stage === 'stage1_clarify') {
    const lowerText = text.toLowerCase().trim();

    // Check for cancel commands
    if (isCancelCommand(text)) {
      const cancelSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`;
      helpers.addSystemMessage({
        text: 'video script generation cancelled.',
        html: generateSpecialMessageTemplate({
          header: { title: 'video script generation cancelled', iconHtml: cancelSvg },
          theme: 'green',
        }),
        sourceTag: 'video_script_status',
      });
      workflows.setIsGeneratingVideoScript(false);
      workflows.resetVideoScriptSession();
      return true;
    }

    // Check for skip command - proceed directly to Stage 2 (writing script spinner will show)
    if (lowerText === 'skip') {
      return await handleVideoScriptStage2(helpers, workflows, []);
    }

    // User provided text input - use it as additional context for Stage 2
    // Create a single clarification entry with the user's full text response
    const userClarifications: VideoScriptClarification[] = [{
      question: 'User-provided context',
      answer: text,
      timestamp: new Date(),
    }];

    // Proceed to Stage 2 with user's context
    return await handleVideoScriptStage2(helpers, workflows, userClarifications);
  }

  // Check for "write a video script [topic]" trigger (only if NOT in stage1_clarify)
  const videoScriptTrigger = parseVideoScriptTrigger(text);
  if (videoScriptTrigger.isVideoScriptTrigger) {
    return await handleVideoScriptFromChat(message, videoScriptTrigger.query, helpers, workflows);
  }

  // Check for cancellation at any stage
  if (workflows.drSessionRef.current.stage !== 'idle' && CANCEL_KEYWORDS.test(text)) {
    helpers.addSystemMessage({
      text: 'deep research cancelled. returning to normal chat.',
      html: `<div style="display:flex;flex-direction:column;gap:8px;margin:4px 0;">
        <strong style="color:#1e40af;font-size:0.95rem;">deep research cancelled</strong>
        <span style="color:#475569;font-size:0.9rem;line-height:1.5;">returning to normal chat.</span>
      </div>`,
      sourceTag: 'deep_research_status',
    });
    workflows.resetDrSession();
    return true;
  }

  // Handle continuation of existing session (stage1_confirm)
  if (workflows.drSessionRef.current.stage === 'stage1_confirm') {
    if (isCancelCommand(text)) {
      helpers.addSystemMessage({
        text: 'understood. deep research cancelled.',
        html: `<div style="display:flex;flex-direction:column;gap:8px;margin:4px 0;">
        <strong style="color:#1e40af;font-size:0.95rem;">understood</strong>
        <span style="color:#475569;font-size:0.9rem;line-height:1.5;">deep research cancelled.</span>
      </div>`,
        sourceTag: 'deep_research_status',
      });
      workflows.resetDrSession();
      return true;
    }

    // User is providing clarifications - proceed to Stage 3
    return await handleStage3DeepResearch(text, message, helpers, workflows);
  }

  // Check for new deep research trigger
  const detection = parseDeepResearchPrompt(text);
  if (!detection.isDeepResearch) {
    // If we're in an active session but message doesn't match patterns, treat as clarification
    if (workflows.drSessionRef.current.stage !== 'idle') {
      workflows.setDrSession((prev) => ({
        ...prev,
        userClarifications: [...prev.userClarifications, text],
      }));
      helpers.addSystemMessage({
        text: 'additional context noted. please answer the clarifying questions above or type "cancel" to exit.',
        html: `<div style="display:flex;flex-direction:column;gap:8px;margin:4px 0;">
        <strong style="color:#1e40af;font-size:0.95rem;">context noted</strong>
        <span style="color:#475569;font-size:0.9rem;line-height:1.5;">please answer the clarifying questions above or type "cancel" to exit.</span>
      </div>`,
        sourceTag: 'deep_research_status',
      });
      return true;
    }
    return false; // Not a deep research query
  }

  const topic = detection.query;
  if (!topic) {
    helpers.addSystemMessage({
      text: 'please include a topic after "deep research".',
      html: `<div style="display:flex;flex-direction:column;gap:8px;margin:4px 0;">
        <strong style="color:#1e40af;font-size:0.95rem;">topic required</strong>
        <span style="color:#475569;font-size:0.9rem;line-height:1.5;">please include a topic after "deep research".</span>
      </div>`,
      sourceTag: 'deep_research_status',
    });
    return true;
  }

  // Start Stage 1 - Qualification
  return await handleStage1DeepResearch(topic, message, helpers, workflows);
}

/**
 * Stage 1: Qualification - Analyze query and determine if deep research is needed
 */
async function handleStage1DeepResearch(
  topic: string,
  message: ChatMessage,
  helpers: WorkflowHelpersInterface,
  workflows: AgenticWorkflowsHook
): Promise<boolean> {
  // 2026-05-23: Avatar/Platform Bridge — announce that deep-research is
  // starting on the active thread. Avatar narrates this out loud to the
  // user if they initiated the workflow from the avatar iframe.
  broadcastBridgeEvent({
    event: 'workflow.started',
    payload: { workflowKind: 'deep-research', title: topic.slice(0, 120) },
  });
  const attachmentCount = message.attachments?.length || 0;
  const hasDocuments = hasAnalyzableFiles(message.attachments);

  let documentAnalysis = '';

  // If user attached documents, analyze them with Claude first
  if (hasDocuments && message.attachments) {
    console.log(`[DeepResearch] Analyzing ${attachmentCount} document(s) with Claude...`);

    const { index: readingIndex, id: readingId } = helpers.streaming.startStreamingMessage({
      text: 'reading attachment…',
      html: getPendingWidgetHtml('reading attachment'),
      sourceTag: 'attachment_read_pending',
    });

    // Allow React to render the pending widget before starting async work
    await new Promise((resolve) => setTimeout(resolve, 0));

    try {
      const analysisResult = await analyzeDocumentsWithClaude({
        files: message.attachments,
        userQuery: topic,
        onProgress: (info) => {
          const label = readingAttachmentLabel(info);
          try {
            helpers.streaming.updateStreamingMessage(readingIndex, {
              text: `${label}…`,
              html: getPendingWidgetHtml(label),
            });
          } catch { /* a progress paint must never break the read */ }
        },
      });

      documentAnalysis = analysisResult.analysis;

      if (analysisResult.tokenUsage) {
        workflows.recordTransaction({
          tokenUsage: analysisResult.tokenUsage,
          source: 'agenticWorkflows',
          modelOverride: buildModelDisplay(analysisResult.tokenUsage, 'Document Analysis', CLAUDE_DOCUMENT_ANALYSIS_DISPLAY),
          summary: 'extracted doc evidence',
        });
      }

      const fileLabel = analysisResult.filesAnalyzed === 1 ? 'attachment' : 'attachments';
      const skippedNote =
        analysisResult.skippedFiles.length > 0
          ? ` (${analysisResult.skippedFiles.length} file${analysisResult.skippedFiles.length === 1 ? '' : 's'} skipped)`
          : '';
      const attachmentTitle = `${analysisResult.filesAnalyzed} ${fileLabel} read${skippedNote}`;

      // Finalize the spinner with the green confirmation card (stops spinner + shows card in one step)
      const completePayload = {
        text: attachmentTitle,
        html: generateSpecialMessageTemplate({
          header: { title: attachmentTitle, icon: '✓' },
          theme: 'green',
        }),
        sourceTag: 'attachment_read_complete',
        tokenUsage: analysisResult.tokenUsage ? { ...analysisResult.tokenUsage, summary: 'document analysis' } : undefined,
      };
      if (helpers.streaming.finalizeStreamingMessageById) {
        helpers.streaming.finalizeStreamingMessageById(readingId, completePayload);
      } else {
        helpers.streaming.finalizeStreamingMessage(readingIndex, completePayload);
      }
    } catch (err) {
      console.error('[DeepResearch] Document analysis failed:', err);
      const errorPayload = {
        text: 'document analysis failed — proceeding with query text only.',
        html: `<div style="padding:8px 12px;background:#fef3c7;border-left:3px solid #f59e0b;border-radius:4px;font-size:13px;color:#92400e;">
          <strong>⚠️ document analysis failed</strong> — proceeding with query text only.
        </div>`,
        sourceTag: 'attachment_read_error',
      };
      // Prefer ID-based finalize
      if (helpers.streaming.finalizeStreamingMessageById) {
        helpers.streaming.finalizeStreamingMessageById(readingId, errorPayload);
      } else {
        helpers.streaming.finalizeStreamingMessage(readingIndex, errorPayload);
      }
    }
  }

  workflows.setDrSessionSync({
    ...INITIAL_DR_SESSION,
    stage: 'stage1',
    originalQuery: topic,
    attachmentContent: documentAnalysis,
  });

  // Build Stage 1 query
  const stage1Query = documentAnalysis
    ? `${topic}\n\n--- DOCUMENT ANALYSIS (from attached files) ---\n${documentAnalysis}`
    : topic;

  // T7/T8 fix (2026-05-11): mirror the user's input language across
  // the entire deep-research workflow. Detect from `topic` (raw user
  // message), NOT `stage1Query` which may include English document analysis.
  const drInputLanguage = detectInputLanguage(topic);
  const stage1SystemPromptLocalized =
    buildLanguageDirective(drInputLanguage) + STAGE1_SYSTEM_PROMPT;

  // Show assessment spinner
  const { index: assessmentIndex, id: assessmentId } = helpers.streaming.startStreamingMessage({
    text: 'assessing…',
    html: getPendingWidgetHtml('assessing'),
    sourceTag: 'stage1_assessment_pending',
  });

  // Allow React to render the pending widget before starting async work
  await new Promise((resolve) => setTimeout(resolve, 0));

  try {
    const result = await runPerplexityResearch({
      query: stage1Query,
      systemPrompt: stage1SystemPromptLocalized,
      // D14 slice 3b: the route name is the routing input — the server stamps
      // the SSOT ladder + effort (mode:'task' on perplexityProxy).
      // model/reasoningEffort feed only the marked deploy-skew fallback.
      route: 'deep-research-qualification',
      model: PERPLEXITY_DEEP_RESEARCH_STAGE1_MODEL,
      reasoningEffort: PERPLEXITY_DEEP_RESEARCH_STAGES_1_2_REASONING_EFFORT,
      searchContextSize: PERPLEXITY_SEARCH_CONTEXT_SIZE,
      // Use direct Cloud Function URL to bypass Firebase Hosting's 60s rewrite timeout.
      // sonar-reasoning-pro with high reasoning effort routinely takes 60-120s.
      proxyUrl: PERPLEXITY_PROXY_URL_DIRECT,
      // Stage 1 is a qualification step — aggressive timeouts to prevent 10+ min hangs.
      // Without these, sonar-reasoning-pro gets 300s timeout × 3 retries + fallback = 18 min worst case.
      timeout: 90000,
      maxRetries: 2,
      skipFallback: true,
    });

    if (result.tokenUsage) {
      workflows.recordTransaction({
        tokenUsage: result.tokenUsage,
        source: 'agenticWorkflows',
        modelOverride: buildModelDisplay(result.tokenUsage, '', PERPLEXITY_DEEP_RESEARCH_STAGE1_DISPLAY),
        summary: 'topic assessment',
      });
    }

    let requiresDeep = true;
    let clarifyingQuestions: string[] = [];
    let initialContext = '';
    let simpleAnswer = '';
    let reasoning = '';

    const parsed = parseStage1Json(result.text || '');
    if (parsed) {
      requiresDeep = parsed.requiresDeepResearch;
      clarifyingQuestions = parsed.clarifyingQuestions || [];
      initialContext = parsed.initialContext || '';
      simpleAnswer = parsed.simpleAnswer || '';
      reasoning = parsed.reasoning || '';
    }

    // Layer 2: If AI returned empty initialContext but deep research is needed,
    // generate query-based initial context immediately to ensure we never show generic text
    if (!initialContext && requiresDeep) {
      console.warn('[Stage1] AI returned empty initialContext, generating query-based summary');
      initialContext = generateQueryBasedInitialContext(stage1Query);
    }

    if (!requiresDeep && simpleAnswer) {
      // Simple question - provide quick answer
      const reasoningNote = reasoning
        ? `<p style="margin-top:8px;color:#64748b;font-size:0.85rem;font-style:italic;">${reasoning}</p>`
        : '';

      const quickAnswerCtaHtml = buildDeepResearchQuickAnswerCtaHtml(message.text || topic);
      const quickAnswerHtml = `<div style="display:flex;flex-direction:column;gap:8px;margin:4px 0;">
        <strong style="color:#1e40af;font-size:0.95rem;">quick answer</strong>
        <div style="color:#0f172a;font-size:0.9rem;line-height:1.5;">${simpleAnswer}</div>
        ${reasoningNote}
        ${quickAnswerCtaHtml}
      </div>`;

      // Prefer ID-based update
      const updatePayload = { text: 'quick answer', html: quickAnswerHtml, sourceTag: 'deep_research_status' };
      if (helpers.streaming.updateStreamingMessageById) {
        helpers.streaming.updateStreamingMessageById(assessmentId, updatePayload);
      } else {
        helpers.streaming.updateStreamingMessage(assessmentIndex, updatePayload);
      }

      // Prefer ID-based finalize
      const finalPayload = {
        text: simpleAnswer,
        html: quickAnswerHtml,
        sourceTag: 'deep_research_quick_answer',
        tokenUsage: result.tokenUsage ? { ...result.tokenUsage, summary: 'quick answer' } : undefined,
      };
      if (helpers.streaming.finalizeStreamingMessageById) {
        helpers.streaming.finalizeStreamingMessageById(assessmentId, finalPayload);
      } else {
        helpers.streaming.finalizeStreamingMessage(assessmentIndex, finalPayload);
      }

      workflows.resetDrSession();
      return true;
    }

    // Deep research is needed - show qualification and questions
    const { accent, accentBg, border } = SPECIAL_MESSAGE_COLORS;

    // Ensure we always have clarifying questions for deep research
    // If API returned none, use fallback questions to gather user context
    if (clarifyingQuestions.length === 0) {
      console.warn('[Stage1] API returned no clarifying questions, using fallback questions');
      clarifyingQuestions = [
        'What specific outcome or goal are you hoping to achieve with this research?',
        'Are there any deadlines, court dates, or time constraints we should be aware of?',
        'Is there any additional context about your specific situation (e.g., current immigration status, prior applications, family circumstances) that would help focus the research?',
      ];
    }

    // Only use initialContext for display (reasoning is kept internal/for logging)
    let displayContext = initialContext?.trim() || '';

    // Layer 3: Final fallback using query-based generation
    // This should rarely execute after Layer 2 catches empty initialContext early
    if (!displayContext) {
      console.warn('[DeepResearch:Legacy] displayContext still empty after Layer 2 - using query-based generation');
      displayContext = generateQueryBasedInitialContext(stage1Query);
    }

    // Pass empty string for reasoning - we no longer display it. Pass
    // language so the "initial findings:" and "how your answers will help:"
    // chrome labels mirror the user's input language.
    const findingsHtml = formatStage1InitialFindings('', displayContext, undefined, drInputLanguage);
    const drChrome = getDeepResearchChromeStrings(drInputLanguage);


    const combinedFindings = `${reasoning}\n\n${initialContext}`;

    // Generate the "a few questions" message using the special message template
    // Split into main HTML (content) and buttons HTML (for selection state processing)
    const mainHtml = generateSpecialMessageTemplate({
      header: {
        icon: '?',
        title: drChrome.qualificationHeader,
      },
      sections: [
        // Inject the pre-formatted findings HTML as a custom section
        { type: 'custom', content: findingsHtml },
        // FINDINGS-FIRST REDESIGN (2026-08-19): the report offer sits between
        // the findings and the questions — same shape as the task-queue card
        // (formatDeepResearchQualificationHtml).
        {
          type: 'custom',
          content: `<div style="font-size:0.9rem;margin-bottom:2px;"><strong>${drChrome.offerTitle}</strong></div><div style="font-size:0.85rem;line-height:1.5;">${drChrome.offerBody}</div>`,
        },
        // Clarifying questions as a list section
        {
          type: 'list',
          title: drChrome.questionsTitle,
          content: clarifyingQuestions,
        },
      ],
      theme: 'blue',
      helperText: drChrome.helperText,
      // NO buttons parameter - buttons will be generated separately
    });

    // Generate buttons HTML separately for workflowActionButtonsHtml field.
    // FINDINGS-FIRST REDESIGN (2026-08-19): ONE button — the video-script
    // secondary is gone from the clarify surfaces (the helper line owns the
    // answer-below affordance; video script remains reachable after delivery).
    const buttonsHtml = generateWorkflowActionButtonsHtml([
      {
        label: drChrome.skipButton,
        id: 'global-tool-deep-research-skip-btn', // Matches BUTTON_IDS.skip in deepResearch.ts
        // Platform palette (2026-08-19): primary CTA rides the deep-gold
        // accent — this also fixes the inverted emphasis this surface kept
        // after the UX 2026-05-14 primary/secondary swap (skip IS the
        // primary action on a clarifying card).
        background: PREST0_PRIMARY_CTA_BG,
      },
    ], { theme: 'blue' });

    // Prefer ID-based finalize
    const qualificationPayload = {
      text: drChrome.qualificationHeader,
      html: mainHtml,  // Main content without buttons
      workflowActionButtonsHtml: buttonsHtml,  // Buttons separately for selection state processing
      sourceTag: 'deep_research_qualification',
      tokenUsage: result.tokenUsage ? { ...result.tokenUsage, summary: 'topic assessment' } : undefined,
    };
    if (helpers.streaming.finalizeStreamingMessageById) {
      helpers.streaming.finalizeStreamingMessageById(assessmentId, qualificationPayload);
    } else {
      helpers.streaming.finalizeStreamingMessage(assessmentIndex, qualificationPayload);
    }

    // Update session state with Stage 1 findings
    // Use setDrSessionSync to prevent race condition where user responds before ref is synced
    workflows.setDrSessionSync((prev) => ({
      ...prev,
      stage: 'stage1_confirm',
      stage1Findings: `${reasoning}\n\n${initialContext}`,
      stage1Questions: clarifyingQuestions,
    }));
    // 2026-05-23: Avatar/Platform Bridge — tell the avatar that Stage 1
    // surfaced clarifying questions so the avatar narrates them out loud,
    // waits for voice answers, collects them, and resubmits to the thread.
    // Include the full Stage 1 findings summary (brief context) AND the
    // complete question list + count so the avatar can enumerate every
    // question and know when collection is complete.
    if (clarifyingQuestions && clarifyingQuestions.length > 0) {
      broadcastBridgeEvent({
        event: 'thread.clarifying-question',
        payload: {
          stage1Summary: (initialContext || reasoning || '').slice(0, 800),
          questions: clarifyingQuestions,
          questionCount: clarifyingQuestions.length,
          stage: 'stage1_confirm',
        },
      });
    }
    // User will respond to questions, then proceed to Stage 3 via stage1_confirm handler
  } catch (error) {
    console.error('Stage 1 qualification failed:', error);
    // Prefer ID-based finalize
    const errorPayload = {
      text: `unable to qualify research: ${formatPerplexityError(error)}`,
      html: `<div style="color:#dc2626;font-weight:600;">unable to qualify research: ${formatPerplexityError(error)}</div>`,
      sourceTag: 'research_error',
    };
    if (helpers.streaming.finalizeStreamingMessageById) {
      helpers.streaming.finalizeStreamingMessageById(assessmentId, errorPayload);
    } else {
      helpers.streaming.finalizeStreamingMessage(assessmentIndex, errorPayload);
    }
    workflows.resetDrSession();
  }

  return true;
}

/**
 * Stage 3: Execute deep research with streaming
 */
async function handleStage3DeepResearch(
  userClarificationText: string,
  message: ChatMessage,
  helpers: WorkflowHelpersInterface,
  workflows: AgenticWorkflowsHook
): Promise<boolean> {
  const allClarifications = [...workflows.drSessionRef.current.userClarifications, userClarificationText];

  const { accent, accentBg, border } = SPECIAL_MESSAGE_COLORS;

  // Analyze any new attachments from the clarification message
  let clarificationDocAnalysis = '';
  if (hasAnalyzableFiles(message.attachments) && message.attachments) {
    const { index: clarificationReadingIndex, id: clarificationReadingId } = helpers.streaming.startStreamingMessage({
      text: 'reading additional attachment…',
      html: getPendingWidgetHtml('reading additional attachment'),
      sourceTag: 'attachment_read_pending',
    });

    // Allow React to render the pending widget before starting async work
    await new Promise((resolve) => setTimeout(resolve, 0));

    try {
      const analysisResult = await analyzeDocumentsWithClaude({
        files: message.attachments,
        userQuery: `Context: User is providing additional documents for research on: ${workflows.drSessionRef.current.originalQuery}`,
      });
      clarificationDocAnalysis = analysisResult.analysis;

      if (analysisResult.tokenUsage) {
        workflows.recordTransaction({
          tokenUsage: analysisResult.tokenUsage,
          source: 'agenticWorkflows',
          modelOverride: buildModelDisplay(analysisResult.tokenUsage, 'Document Analysis', CLAUDE_DOCUMENT_ANALYSIS_DISPLAY),
          summary: 'parsed follow-up docs',
        });
      }

      const clarificationFileLabel = analysisResult.filesAnalyzed === 1 ? 'attachment' : 'attachments';
      const clarificationTitle = `${analysisResult.filesAnalyzed} additional ${clarificationFileLabel} read`;

      // Finalize the spinner with the green confirmation card (stops spinner + shows card in one step)
      const completePayload = {
        text: clarificationTitle,
        html: generateSpecialMessageTemplate({
          header: { title: clarificationTitle, icon: '✓' },
          theme: 'green',
        }),
        sourceTag: 'attachment_read_complete',
        tokenUsage: analysisResult.tokenUsage ? { ...analysisResult.tokenUsage, summary: 'document analysis' } : undefined,
      };
      if (helpers.streaming.finalizeStreamingMessageById) {
        helpers.streaming.finalizeStreamingMessageById(clarificationReadingId, completePayload);
      } else {
        helpers.streaming.finalizeStreamingMessage(clarificationReadingIndex, completePayload);
      }
    } catch (err) {
      console.error('[DeepResearch] Clarification doc analysis failed:', err);
      // Prefer ID-based finalize
      const errorPayload = {
        text: 'additional document analysis failed — proceeding with existing context.',
        html: `<div style="padding:8px 12px;background:#fef3c7;border-left:3px solid #f59e0b;border-radius:4px;font-size:13px;color:#92400e;">
          <strong>⚠️ additional document analysis failed</strong> — proceeding with existing context.
        </div>`,
        sourceTag: 'attachment_read_error',
      };
      if (helpers.streaming.finalizeStreamingMessageById) {
        helpers.streaming.finalizeStreamingMessageById(clarificationReadingId, errorPayload);
      } else {
        helpers.streaming.finalizeStreamingMessage(clarificationReadingIndex, errorPayload);
      }
    }
  }

  const combinedAttachmentContent = workflows.drSessionRef.current.attachmentContent
    ? clarificationDocAnalysis
      ? `${workflows.drSessionRef.current.attachmentContent}\n\n--- ADDITIONAL DOCUMENTS (from clarification) ---\n${clarificationDocAnalysis}`
      : workflows.drSessionRef.current.attachmentContent
    : clarificationDocAnalysis;

  workflows.setDrSessionSync((prev) => ({
    ...prev,
    stage: 'stage3',
    userClarifications: allClarifications,
    attachmentContent: combinedAttachmentContent,
  }));

  // Show pending widget for deep research
  const { index: streamingIndex, id: streamingId } = helpers.streaming.startStreamingMessage({
    text: '',
    html: getPendingWidgetHtml('deep researching'),
    sourceTag: 'research_result_streaming',
  });

  // IMPORTANT: Allow React to render the pending widget before starting async work.
  // Without this yield, Stage 2 may complete before React renders, causing the
  // pending widget to never display (it gets replaced by streaming content).
  await new Promise((resolve) => setTimeout(resolve, 0));

  try {
    // Stage 2: Build research brief internally
    const documentAnalysisSection = combinedAttachmentContent
      ? `\n\n--- DOCUMENT ANALYSIS (from attached files) ---\n${combinedAttachmentContent}`
      : '';
    const stage2Query = `Original Query: ${workflows.drSessionRef.current.originalQuery}\n\nUser Clarifications: ${allClarifications.join('\n')}\n\nStage 1 Findings: ${workflows.drSessionRef.current.stage1Findings}\n\nStage 1 Questions Asked: ${workflows.drSessionRef.current.stage1Questions.join('; ')}${documentAnalysisSection}`;

    // T7/T8 fix (2026-05-11): same language-mirror policy for Stage 2.
    const stage2InputLanguage = detectInputLanguage(
      workflows.drSessionRef.current.originalQuery || ''
    );
    const stage2SystemPromptLocalized =
      buildLanguageDirective(stage2InputLanguage) + STAGE2_SYSTEM_PROMPT;

    const stage2Result = await runPerplexityResearch({
      query: stage2Query,
      systemPrompt: stage2SystemPromptLocalized,
      // D14 slice 3b: route name is the routing input (stage 2 rides the
      // qualification pre-stage tier); model/reasoningEffort feed only the
      // deploy-skew fallback.
      route: 'deep-research-qualification',
      model: PERPLEXITY_DEEP_RESEARCH_STAGE2_MODEL,
      reasoningEffort: PERPLEXITY_DEEP_RESEARCH_STAGES_1_2_REASONING_EFFORT,
      // Use direct proxy to bypass Firebase Hosting's 60s rewrite timeout
      proxyUrl: PERPLEXITY_PROXY_URL_DIRECT,
      // Stage 2 builds full research strategy from Stage 1 findings + document analysis.
      // sonar-reasoning-pro with high reasoning + 15-25K token payload can take 2-4 min.
      // Match the Cloud Function's 300s timeout to avoid client-side early abort.
      timeout: 300000,
      maxRetries: 2,
      skipFallback: true,
    });

    if (stage2Result.tokenUsage) {
      workflows.recordTransaction({
        tokenUsage: stage2Result.tokenUsage,
        source: 'agenticWorkflows',
        modelOverride: buildModelDisplay(stage2Result.tokenUsage, '', PERPLEXITY_DEEP_RESEARCH_STAGE2_DISPLAY),
        summary: 'built research strategy',
      });
    }

    let brief = '';
    let additionalContext = '';
    try {
      const parsed = JSON.parse(stage2Result.text);
      brief = parsed.researchBrief || stage2Result.text;
      additionalContext = parsed.additionalContext || '';
    } catch {
      brief = stage2Result.text;
    }

    const stage2Brief = brief + (additionalContext ? `\n\nAdditional Context: ${additionalContext}` : '');

    // Stage 3: Run full deep research with streaming
    const documentsSection = combinedAttachmentContent
      ? `\n\n--- DOCUMENT ANALYSIS (from attached files) ---\n${combinedAttachmentContent}`
      : '';

    const fullContext = `
RESEARCH BRIEF:
${stage2Brief}

ORIGINAL USER QUERY:
${workflows.drSessionRef.current.originalQuery}

USER-PROVIDED CLARIFICATIONS:
${allClarifications.join('\n')}

STAGE 1 PRELIMINARY FINDINGS:
${workflows.drSessionRef.current.stage1Findings}${documentsSection}

Unless otherwise specified, for formatting and style, ignore all inputs (query, attachments, documents, etc.) and use professional, organized formatting throughout the report while still reading all information, attachment, and documents given for info.
`.trim();

    let finalText = '';
    let researchTokenUsage: TokenUsageMetadata | undefined;
    let perplexityCitations: string[] | undefined;

    // MT-QA-BUG-001: resolve the active tenant's firm branding once so the
    // system prompt doesn't self-identify as "The Law Offices of Fernando
    // Hidalgo, Inc." for T1/T2 tenants. Reused later for PDF/DOCX branding.
    const drSystemBranding = await fetchCurrentTenantBranding();

    // Stage 3: Streaming deep research
    // model controlled by TESTING_MODE in agenticWorkflowConfig.ts
    // Uses direct Cloud Function URL to bypass Firebase Hosting's 60s rewrite timeout.
    let lastStreamUpdate = 0;
    const STREAM_THROTTLE_MS = 500;

    // T7/T8 fix (2026-05-11): same language-mirror policy for Stage 3,
    // the final user-facing report. Detect from original query, not fullContext.
    const stage3InputLanguage = detectInputLanguage(
      workflows.drSessionRef.current.originalQuery || ''
    );
    const stage3SystemPromptLocalized =
      buildLanguageDirective(stage3InputLanguage) +
      buildDeepResearchSystemPrompt(fullContext, { firmName: drSystemBranding.firmName });

    const result = await runPerplexityResearchStream({
      query: 'Generate the comprehensive research report based on the research brief, original query, clarifications, and preliminary findings provided in the system instructions.',
      systemPrompt: stage3SystemPromptLocalized,
      // D14 slice 3b: route name is the routing input — the server stamps the
      // tier-14 ladder + effort ('high' lives in the SSOT now). The model
      // below feeds only the deploy-skew fallback (TESTING_MODE's cheaper
      // model likewise applies only there — model choice is the server's).
      route: 'deep-research',
      model: PERPLEXITY_DEEP_RESEARCH_MODEL,
      maxTokens: PERPLEXITY_DEEP_RESEARCH_MAX_TOKENS,
      searchContextSize: 'high',
      reasoningEffort: PERPLEXITY_REASONING_EFFORT,
      // NOTE: temperature intentionally NOT passed for sonar-deep-research.
      proxyUrl: PERPLEXITY_PROXY_URL_DIRECT,
      onChunk: (_chunk: string, accumulated: string) => {
        const visibleText = stripThinkingBlocks(accumulated);
        if (!visibleText.trim()) return;

        const now = Date.now();
        if (now - lastStreamUpdate < STREAM_THROTTLE_MS) return;
        lastStreamUpdate = now;

        const updatePayload = {
          text: visibleText,
          html: formatResearchToHtml(visibleText),
          sourceTag: 'research_streaming',
        };
        if (helpers.streaming.updateStreamingMessageById) {
          helpers.streaming.updateStreamingMessageById(streamingId, updatePayload);
        } else {
          helpers.streaming.updateStreamingMessage(streamingIndex, updatePayload);
        }
      },
    });

    finalText = result.text || '';
    researchTokenUsage = result.tokenUsage;
    perplexityCitations = result.citations;

    if (researchTokenUsage) {
      workflows.recordTransaction({
        tokenUsage: researchTokenUsage,
        source: 'agenticWorkflows',
        modelOverride: buildModelDisplay(researchTokenUsage, '', PERPLEXITY_DEEP_RESEARCH_DISPLAY),
        summary: 'comprehensive case study',
      });
    }

    // DIAGNOSTIC: Log raw vs stripped content breakdown to understand think block ratio
    const rawText = finalText;
    const thinkBlocks: string[] = rawText.match(/<think>[\s\S]*?<\/think>/gi) || [];
    const thinkContentLength = thinkBlocks.reduce((sum: number, b: string) => sum + b.length, 0);
    console.log('[DeepResearch] Content breakdown:', {
      rawLength: rawText.length,
      thinkBlockCount: thinkBlocks.length,
      thinkContentChars: thinkContentLength,
      visibleContentChars: rawText.length - thinkContentLength,
      outputTokens: researchTokenUsage?.outputTokens,
      reasoningTokens: researchTokenUsage?.reasoningTokens,
      finishReason: researchTokenUsage?.finishReason,
      maxTokensSent: PERPLEXITY_DEEP_RESEARCH_MAX_TOKENS,
      completionVsMax: `${researchTokenUsage?.outputTokens || 0} / ${PERPLEXITY_DEEP_RESEARCH_MAX_TOKENS}`,
    });

    // Clean the content and ensure references section exists
    const rawContent = stripThinkingBlocks(rawText);
    let cleanedContent = ensureReferencesSection(
      rawContent,
      perplexityCitations
    );

    // dr-truncated-refs-repair-v20260815: on finish_reason=length the cut lands
    // in the LAST section — References. A partially-emitted list passes
    // ensureReferencesSection's non-empty gate (>=80 chars or any URL) and
    // previously shipped exactly as truncated, discarding the authoritative
    // citations[] array. Additively append every citation URL missing from the
    // (possibly cut) section, continuing the numbering.
    if (researchTokenUsage?.finishReason === 'length') {
      const refsRepair = appendMissingCitationsToReferences(cleanedContent, perplexityCitations);
      if (refsRepair.appended > 0) {
        console.log(
          `[DeepResearch] dr-truncated-refs-repair-v20260815 (desktop path): appended ${refsRepair.appended} citation(s) missing from the truncated References section`
        );
        cleanedContent = refsRepair.content;
      }
    }

    // MT-QA-BUG-001 follow-up (2026-05-08 §132a desktop regression): port
    // the widget-path post-processors to the account /desktop pipeline so
    // Fernando-account WC / employment / general reports don't ship with
    // (a) letter-prefixed category subheaders in the References list or
    // (b) stray immigration boilerplate in the closing Important Notice /
    // References. Classify the niche from the original query + aggregated
    // research context, then apply both transforms before the PDF/DOCX
    // generators see the content. See
    // `src/services/agenticWorkflows/reportPostProcess.ts`.
    try {
      const { classifyNiche, stripImmigrationBleed, flattenAndDeStubReferences, reconcileCaseLawAuthorities } = await import('./reportPostProcess');
      const classifiedNiche = classifyNiche(
        workflows.drSessionRef.current.originalQuery || '',
        `${workflows.drSessionRef.current.stage1Findings || ''}\n${stage2Brief || ''}`,
      );
      const beforeBleed = cleanedContent;
      cleanedContent = stripImmigrationBleed(cleanedContent, classifiedNiche);
      const bleedStripped = beforeBleed !== cleanedContent;

      const refsFlatten = flattenAndDeStubReferences(cleanedContent);
      if (refsFlatten.changed) {
        cleanedContent = refsFlatten.content;
      }

      // DR-1 §132a regression (2026-06-29): reconcile the model's plain-text
      // "References and authorities cited" list against the case law the
      // body actually relies on. The model emits this list without a
      // markdown heading, so the two processors above never touch it and it
      // ships statute-only — omitting the controlling cases (Honeywell,
      // Rodriguez, Williams) the analysis depends on. This guarantees every
      // body-cited case appears in the list, repairs bare "§ N" fragments,
      // and renumbers. See reportPostProcess.ts::reconcileCaseLawAuthorities.
      const authReconcile = reconcileCaseLawAuthorities(cleanedContent);
      if (authReconcile.changed) {
        cleanedContent = authReconcile.content;
      }
      console.log('[AgenticWorkflows] Deep research post-processors applied', {
        classifiedNiche,
        bleedStripped,
        refsChanged: refsFlatten.changed,
        stubsRemoved: refsFlatten.stubsRemoved,
        categoriesRemoved: refsFlatten.categoriesRemoved,
        authReconciled: authReconcile.changed,
        casesAdded: authReconcile.casesAdded,
        fragmentsRepaired: authReconcile.fragmentsRepaired,
        authDuplicatesRemoved: authReconcile.duplicatesRemoved,
      });
    } catch (postProcessError) {
      console.warn('[AgenticWorkflows] Post-processor threw, continuing with raw content:', postProcessError);
    }

    const reportTitle = generateConciseTitle(cleanedContent, workflows.drSessionRef.current.originalQuery);

    // Build PDF and DOCX with Firebase Storage upload for cross-device persistence
    // Get current user UID for Storage path
    const auth = getAuth();
    const currentUid = auth.currentUser?.uid;

    // MT-QA-BUG-001: reuse tenant branding resolved earlier in this handler.
    const drTenantBranding = drSystemBranding;

    // Wrap document generation in try-catch to handle failures gracefully
    // If document generation fails, we still want to finalize with the research content
    let pdf: File | undefined;
    let docx: File | undefined;
    let finalPdfSerialized: SerializedAttachment | undefined;
    let finalDocxSerialized: SerializedAttachment | undefined;
    let documentGenerationFailed = false;

    // Log before starting document generation
    console.log('[AgenticWorkflows] Starting document generation...', {
      contentLength: cleanedContent?.length,
      reportTitle,
      currentUid: currentUid ? '(authenticated)' : '(not authenticated)',
    });

    try {
      const {
        pdf: generatedPdf,
        docx: generatedDocx,
        pdfSerialized,
        docxSerialized,
        pdfSerializedWithStorage,
        docxSerializedWithStorage,
        uploadStatus,
      } = await generateDualFormatDocumentsWithStorage(
        {
          type: 'research',
          title: reportTitle,
          prompt: '',
          content: cleanedContent,
          translations: { firmName: drTenantBranding.firmName },
        },
        // Only upload to Storage if user is authenticated
        currentUid ? { uid: currentUid, messageId: `dr-${Date.now()}` } : undefined
      );

      pdf = generatedPdf;
      docx = generatedDocx;

      // Log upload status for debugging cross-device persistence issues
      if (uploadStatus) {
        if (uploadStatus.error) {
          console.warn('[AgenticWorkflows] Storage upload issue:', uploadStatus.error, {
            pdfUploaded: uploadStatus.pdfUploaded,
            docxUploaded: uploadStatus.docxUploaded,
          });
        } else if (uploadStatus.pdfUploaded && uploadStatus.docxUploaded) {
          console.log('[AgenticWorkflows] Storage upload successful - attachments will persist cross-device');
        }
      }

      // Use Storage-backed serialized attachments if available, fallback to localStorage-only
      finalPdfSerialized = pdfSerializedWithStorage || pdfSerialized;
      finalDocxSerialized = docxSerializedWithStorage || docxSerialized;

      // Validate that final attachments have downloadable content
      const pdfHasContent = !!(finalPdfSerialized?.storageUrl || finalPdfSerialized?.dataUrl);
      const docxHasContent = !!(finalDocxSerialized?.storageUrl || finalDocxSerialized?.dataUrl);
      if (!pdfHasContent) {
        console.error('[AgenticWorkflows] PDF attachment has no downloadable content');
      }
      if (!docxHasContent) {
        console.error('[AgenticWorkflows] DOCX attachment has no downloadable content');
      }

      console.log('[AgenticWorkflows] Document generation successful:', {
        pdfName: pdf?.name,
        docxName: docx?.name,
        pdfHasContent,
        docxHasContent,
      });
    } catch (docGenError) {
      // Document generation failed - log error but continue with finalization
      // Research content is still valuable even without downloadable documents
      documentGenerationFailed = true;
      console.error('[AgenticWorkflows] Document generation failed:', docGenError);
      console.warn('[AgenticWorkflows] Continuing without PDF/DOCX attachments - research content will still be displayed');
    }

    // Build attachments arrays only if document generation succeeded
    const finalAttachments: File[] | undefined =
      pdf && docx && !documentGenerationFailed ? [pdf, docx] : undefined;
    const finalAttachmentsSerialized =
      finalPdfSerialized && finalDocxSerialized && !documentGenerationFailed
        ? [finalPdfSerialized, finalDocxSerialized]
        : undefined;

    // Finalize the streaming message with combined token usages from both stages
    const finalHtml = formatResearchToHtml(cleanedContent);

    // Collect Stage 2 (research brief) and Stage 3 (deep research) token usages with summaries
    const allTokenUsages: TokenUsageMetadata[] = [];
    if (stage2Result.tokenUsage) {
      allTokenUsages.push({
        ...stage2Result.tokenUsage,
        summary: 'research strategy',
      });
    }
    const stage3Usage = researchTokenUsage || result.tokenUsage;
    if (stage3Usage) {
      allTokenUsages.push({
        ...stage3Usage,
        summary: 'deep research',
      });
    }

    // 2026-05-23: Avatar/Platform Bridge — deep-research is entering its
    // synthesis phase. Avatar narrates progress.
    broadcastBridgeEvent({
      event: 'workflow.progress',
      payload: {
        stage: 'stage3_synthesis',
        percent: 66,
        summary: 'synthesizing findings into final report',
      },
    });
    // Debug logging: verify token and attachment data before finalize
    console.log('[AgenticWorkflows] Pre-finalize state:', {
      // Token usage verification
      tokenUsagesCount: allTokenUsages.length,
      tokenUsages: allTokenUsages.map(u => ({
        model: u.model,
        provider: u.provider,
        hasKnownPricing: hasKnownPricing(u.model),
        inputTokens: u.inputTokens,
        outputTokens: u.outputTokens,
        summary: u.summary,
      })),
      // Attachment verification
      pdfExists: !!pdf,
      docxExists: !!docx,
      documentGenerationFailed,
      finalAttachmentsLength: finalAttachments?.length,
      finalSerializedLength: finalAttachmentsSerialized?.length,
      pdfSerializedHasContent: !!(finalPdfSerialized?.storageUrl || finalPdfSerialized?.dataUrl),
      docxSerializedHasContent: !!(finalDocxSerialized?.storageUrl || finalDocxSerialized?.dataUrl),
    });

    // FIX (Point 5, 2026-06-02): finalize the FINAL report by ID, not by index.
    // A deep-research pass runs 3-5 min; during that window the message array
    // shifts (cross-tab sync / hydration / other messages), so `streamingIndex`
    // drifts and an index-based finalize lands on the wrong message — leaving the
    // actual report bubble WITHOUT tokenUsages, so its "i" popout shows
    // "no extensive compute was necessary" even though the wallet was charged.
    // Every other workflow in this file already prefers the race-immune ById path;
    // the report finalize was the lone index-based exception. Mirror the guard.
    // ONE MESSAGE PER DELIVERABLE (owner redundancy directive 2026-08-29):
    // the follow-up buttons ride the report message itself — the separate
    // "your report is ready" tile restated what the report above it already
    // showed. Same shape as the modern deep-research lane's finalize.
    const translateBtnId = `${CHAT_TRANSLATE_BUTTON_ID}-research`;
    const reportActionButtonsHtml = generateWorkflowActionButtonsHtml([
      { label: 'translate to spanish', id: translateBtnId, background: PREST0_SECONDARY_CTA_BG },
      { label: 'write video script', id: GLOBAL_VIDEO_SCRIPT_BUTTON_IDS.videoScript, background: PREST0_SECONDARY_CTA_BG },
    ]);
    const reportFinalPayload = {
      text: cleanedContent,
      html: finalHtml,
      sourceTag: 'research_result',
      workflowActionButtonsHtml: reportActionButtonsHtml,
      // Only include attachments if document generation succeeded
      attachments: finalAttachments,
      // Use Storage-backed attachments for cross-device download support
      attachmentsSerialized: finalAttachmentsSerialized,
      // Pass both stages' token usages for multi-model display in info popup
      tokenUsages: allTokenUsages.length > 0 ? allTokenUsages : undefined,
      // Also keep single tokenUsage for backward compatibility
      tokenUsage: researchTokenUsage || result.tokenUsage,
    };
    if (helpers.streaming.finalizeStreamingMessageById) {
      helpers.streaming.finalizeStreamingMessageById(streamingId, reportFinalPayload);
    } else {
      helpers.streaming.finalizeStreamingMessage(streamingIndex, reportFinalPayload);
    }

    // 2026-05-23: Avatar/Platform Bridge — research is complete and the
    // report + deliverables are on the thread. Narrate to the user.
    const deliverableUrl =
      finalPdfSerialized?.storageUrl || finalPdfSerialized?.dataUrl || null;
    broadcastBridgeEvent({
      event: 'workflow.completed',
      payload: {
        workflowKind: 'deep-research',
        summary: cleanedContent.slice(0, 240),
        deliverableUrl,
        reportTitle: reportTitle || workflows.drSessionRef.current.originalQuery || '',
      },
    });

    // Store the completed report
    const reportData = {
      content: cleanedContent,
      title: reportTitle,
      timestamp: new Date(),
    };
    workflows.setLastResearchReport(reportData);
    workflows.lastResearchReportRef.current = reportData;

    // Store as translatable content
    const translatableResearchData = {
      content: cleanedContent,
      title: reportTitle,
      timestamp: new Date(),
      source: 'research' as const,
    };
    workflows.setLastTranslatableContent(translatableResearchData);
    workflows.lastTranslatableContentRef.current = translatableResearchData;

  } catch (error) {
    console.error('research failed', error);
    // Use finalizeStreamingMessage (not updateStreamingMessage) to properly clear processing state
    // This ensures the chat doesn't get stuck in "thinking" state on errors
    helpers.streaming.finalizeStreamingMessage(streamingIndex, {
      text: `unable to complete research: ${formatPerplexityError(error)}`,
      html: `<div style="color:#dc2626;font-weight:600;">unable to complete research: ${formatPerplexityError(error)}</div>`,
      sourceTag: 'research_error',
    });
  }

  workflows.resetDrSession();
  return true;
}

// ============================================================================
// Video Script Handler
// ============================================================================

/**
 * Handle "Write Video Script" button click.
 * Initiates the video script generation workflow using the latest available content.
 */
export async function handleWriteVideoScript(
  helpers: WorkflowHelpersInterface,
  workflows: AgenticWorkflowsHook
): Promise<boolean> {
  // Check if already generating
  if (workflows.isGeneratingVideoScript) {
    const hourglassSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 22h14"></path><path d="M5 2h14"></path><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"></path><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"></path></svg>`;
    helpers.addSystemMessage({
      text: 'video script generation is already in progress. please wait for it to complete.',
      html: generateSpecialMessageTemplate({
        header: { title: 'please wait', iconHtml: hourglassSvg },
        sections: [
          { type: 'text', content: 'video script generation is already in progress.', style: { fontSize: '0.9rem' } },
        ],
        theme: 'green',
      }),
      sourceTag: 'video_script_status',
    });
    return true;
  }

  // Determine source content (prefer most recent: translated > formatted > research)
  let sourceContent: VideoScriptSourceContent | null = null;

  // Check translatable content (could be formatted or research)
  let translatableContent = workflows.lastTranslatableContentRef.current || workflows.lastTranslatableContent;
  let researchReport = workflows.lastResearchReportRef.current || workflows.lastResearchReport;

  // Fallback: Try to recover content from persisted chat history
  if (!translatableContent && !researchReport) {
    const history = getFromLocalStorage<ChatMessage[]>(GLOBAL_CHAT_HISTORY_KEY, []);
    // Try formatted content first, then research report
    const formatted = extractLatestWorkflowOutput(history, 'formatted_content');
    const research = extractLatestWorkflowOutput(history, 'research_report');
    const historyOutput = formatted || research;

    if (historyOutput) {
      translatableContent = {
        content: historyOutput.content,
        title: historyOutput.title || 'Content',
        timestamp: new Date(historyOutput.timestamp),
        source: historyOutput.type === 'formatted_content' ? 'formatted' : 'research',
      };
      // Update refs for future use
      workflows.setLastTranslatableContent(translatableContent);
      console.log('[AgenticWorkflows] Recovered content from chat history for video script');
    }
  }

  if (translatableContent) {
    sourceContent = {
      content: translatableContent.content,
      title: translatableContent.title,
      source: translatableContent.source,
      timestamp: translatableContent.timestamp,
    };
  } else if (researchReport) {
    sourceContent = {
      content: researchReport.content,
      title: researchReport.title,
      source: 'research',
      timestamp: researchReport.timestamp,
    };
  }

  // No content available
  if (!sourceContent || !sourceContent.content) {
    const alertSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>`;
    helpers.addSystemMessage({
      text: 'no content available to create a video script. please run deep research first, then click "write video script".',
      html: generateSpecialMessageTemplate({
        header: { title: 'no content available', iconHtml: alertSvg },
        sections: [
          {
            type: 'text',
            content: 'run a deep research query first, then click <strong>write video script</strong> in the results to generate a video script from the content.',
            style: { fontSize: '0.9rem', custom: 'line-height:1.5;' },
          },
        ],
        theme: 'green',
      }),
      sourceTag: 'video_script_status',
    });
    return true;
  }

  // Reset deep research session to prevent GlobalTools from intercepting video script clarifications
  // (GlobalTools processes messages FIRST, so if drSession is in 'stage1_confirm', it would
  // intercept user replies meant for video script stage1_clarify)
  workflows.resetDrSession();

  // Set generating state
  workflows.setIsGeneratingVideoScript(true);

  // Initialize video script session (use sync setter to prevent race conditions)
  workflows.setVideoScriptSessionSync(() => ({
    stage: 'stage1_analyzing',
    sourceContent,
    stage1Analysis: null,
    userClarifications: [],
    stage2Output: null,
    startedAt: new Date(),
    completedAt: null,
  }));

  // Start streaming message with pending widget (uses overlay pattern - not in message box)
  const { index: streamingIndex, id: streamingId } = helpers.streaming.startStreamingMessage({
    text: 'reading content...',
    html: getPendingWidgetHtml('reading content'),
    sourceTag: 'video_script_analysis_pending',
  });

  try {
    // Call Claude 4.5 Sonnet for Stage 1 analysis
    const result = await runVideoScriptAnalysis({
      sourceContent: sourceContent.content,
      sourceTitle: sourceContent.title,
      sourceType: sourceContent.source,
      onChunk: (chunk, accumulated) => {
        // Update streaming message with partial content (keep pending widget visible)
        helpers.streaming.updateStreamingMessage(streamingIndex, {
          text: `analyzing... ${accumulated.length} chars`,
          html: getPendingWidgetHtml('analyzing'),
        });
      },
      onComplete: (fullText, tokenUsage) => {
        // Record token usage for Stage 1
        if (tokenUsage) {
          workflows.recordTransaction({
            tokenUsage: { ...tokenUsage, summary: 'video script analysis' },
            source: 'video_script_stage1',
            modelOverride: buildModelDisplay(tokenUsage, 'Script Analysis', PERPLEXITY_VIDEO_SCRIPT_STAGE1_DISPLAY),
            summary: 'video script analysis',
          });
        }
      },
    });

    // Parse the Stage 1 response
    const stage1Result = parseVideoScriptStage1Response(result.text);

    // Update session with Stage 1 result (use sync setter to prevent race condition with user reply)
    workflows.setVideoScriptSessionSync(prev => ({
      ...prev,
      stage: stage1Result.needsClarification ? 'stage1_clarify' : 'stage2_generating',
      stage1Analysis: stage1Result,
    }));

    if (stage1Result.needsClarification) {
      // Finalize streaming message directly with clarifying questions (no intermediate message)
      const questionsHtml = formatVideoScriptClarifyingQuestionsHtml(
        stage1Result.summary.topic,
        stage1Result.clarifyingQuestions,
        GLOBAL_VIDEO_SCRIPT_BUTTON_IDS.proceed
      );

      helpers.streaming.finalizeStreamingMessage(streamingIndex, {
        text: 'please answer the following questions to optimize your video script.',
        html: questionsHtml,
        sourceTag: 'video_script_questions',
        tokenUsage: result.tokenUsage ? { ...result.tokenUsage, summary: 'video script analysis' } : undefined,
      });

      // Keep generating flag on since we're waiting for user text input
      // User's next message will be intercepted and used for Stage 2
    } else {
      // No clarification needed - proceed directly to Stage 2 without intermediate message
      // The "ready to generate" message is unnecessary visual clutter
      helpers.streaming.finalizeStreamingMessage(streamingIndex, {
        text: '',
        html: '',
        sourceTag: 'video_script_analysis_complete',
      });

      // Call Stage 2 to generate the script
      await handleVideoScriptStage2(helpers, workflows, []);
    }
  } catch (error) {
    console.error('video script analysis failed', error);
    const errorSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`;
    helpers.streaming.finalizeStreamingMessage(streamingIndex, {
      text: `unable to analyze content: ${formatVideoScriptError(error)}`,
      html: generateSpecialMessageTemplate({
        header: { title: 'unable to analyze content', iconHtml: errorSvg },
        sections: [
          { type: 'text', content: formatVideoScriptError(error), style: { fontSize: '0.9rem' } },
        ],
        theme: 'custom',
        customColors: RED_ERROR_THEME,
      }),
      sourceTag: 'video_script_error',
    });

    // Reset state on error
    workflows.setIsGeneratingVideoScript(false);
    workflows.resetVideoScriptSession();
  }

  return true;
}

/**
 * Handle "write a video script [topic]" chat message trigger.
 * Allows users to type the command to start video script workflow with their topic and optional attachments.
 */
export async function handleVideoScriptFromChat(
  message: ChatMessage,
  query: string,
  helpers: WorkflowHelpersInterface,
  workflows: AgenticWorkflowsHook
): Promise<boolean> {
  const { accent, accentBg, border, subtitle } = VIDEO_SCRIPT_COLORS;

  // Check if already generating
  if (workflows.isGeneratingVideoScript) {
    const hourglassSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 22h14"></path><path d="M5 2h14"></path><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"></path><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"></path></svg>`;
    helpers.addSystemMessage({
      text: 'video script generation is already in progress. please wait for it to complete.',
      html: generateSpecialMessageTemplate({
        header: { title: 'please wait', iconHtml: hourglassSvg },
        sections: [
          { type: 'text', content: 'video script generation is already in progress.', style: { fontSize: '0.9rem' } },
        ],
        theme: 'green',
      }),
      sourceTag: 'video_script_status',
    });
    return true;
  }

  // Analyze attachments if present
  let attachmentAnalysis = '';
  if (hasAnalyzableFiles(message.attachments) && message.attachments) {
    // Show "reading attachment" spinner
    const { index: attachmentIndex, id: attachmentId } = helpers.streaming.startStreamingMessage({
      text: 'reading attachment...',
      html: getPendingWidgetHtml('reading attachment'),
      sourceTag: 'video_script_attachment_pending',
    });

    try {
      const analysisResult = await analyzeDocumentsWithClaude({
        files: message.attachments,
        userQuery: query || 'Analyze this document for video script creation',
        onProgress: (info) => {
          const label = readingAttachmentLabel(info);
          try {
            helpers.streaming.updateStreamingMessage(attachmentIndex, {
              text: `${label}...`,
              html: getPendingWidgetHtml(label),
            });
          } catch { /* a progress paint must never break the read */ }
        },
      });
      attachmentAnalysis = analysisResult.analysis;

      // Record token usage for document analysis
      if (analysisResult.tokenUsage) {
        workflows.recordTransaction({
          tokenUsage: analysisResult.tokenUsage,
          source: 'agenticWorkflows',
          modelOverride: buildModelDisplay(analysisResult.tokenUsage, 'Document Analysis', CLAUDE_DOCUMENT_ANALYSIS_DISPLAY),
          summary: 'document analysis for video script',
        });
      }

      // Finalize with success
      const checkSvg = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"></path></svg>`;
      const fileCount = message.attachments.length;
      helpers.streaming.finalizeStreamingMessage(attachmentIndex, {
        text: `${fileCount} attachment${fileCount > 1 ? 's' : ''} read successfully`,
        html: generateSpecialMessageTemplate({
          header: { title: `${fileCount} attachment${fileCount > 1 ? 's' : ''} read successfully`, iconHtml: checkSvg },
          theme: 'green',
        }),
        sourceTag: 'video_script_attachment_complete',
      });
    } catch (attachError) {
      console.warn('attachment analysis failed, proceeding with text only', attachError);
      const warnSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>`;
      helpers.streaming.finalizeStreamingMessage(attachmentIndex, {
        text: 'document analysis failed - proceeding with query text only',
        html: generateSpecialMessageTemplate({
          header: { title: 'document analysis failed - proceeding with query text only', iconHtml: warnSvg },
          theme: 'custom',
          customColors: AMBER_THEME,
        }),
        sourceTag: 'video_script_attachment_warning',
      });
    }
  }

  // Combine content from query and attachment analysis
  const combinedContent = attachmentAnalysis
    ? `${query}\n\n--- Document Analysis ---\n${attachmentAnalysis}`
    : query;

  // Validate we have actual content
  if (!combinedContent.trim()) {
    const alertSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>`;
    helpers.addSystemMessage({
      text: 'please provide a topic after "write a video script" or attach documents to analyze.',
      html: generateSpecialMessageTemplate({
        header: { title: 'topic needed', iconHtml: alertSvg },
        sections: [
          { type: 'text', content: 'please provide a topic after "write a video script" or attach documents to analyze.', style: { fontSize: '0.9rem' } },
          { type: 'text', content: 'example: "write a video script about H-1B visa process"', style: { fontSize: '0.85rem', custom: 'color:#64748b;' } },
        ],
        theme: 'green',
      }),
      sourceTag: 'video_script_status',
    });
    return true;
  }

  // Reset deep research session to prevent GlobalTools from intercepting video script clarifications
  // (GlobalTools processes messages FIRST, so if drSession is in 'stage1_confirm', it would
  // intercept user replies meant for video script stage1_clarify)
  workflows.resetDrSession();

  // Create source content for video script workflow
  const sourceContent: VideoScriptSourceContent = {
    content: combinedContent,
    title: query.slice(0, 100) || 'Custom Video Script',
    source: 'research',
    timestamp: new Date(),
  };

  // Set generating state
  workflows.setIsGeneratingVideoScript(true);

  // Initialize video script session (use sync setter to prevent race conditions)
  workflows.setVideoScriptSessionSync(() => ({
    stage: 'stage1_analyzing',
    sourceContent,
    stage1Analysis: null,
    userClarifications: [],
    stage2Output: null,
    startedAt: new Date(),
    completedAt: null,
  }));

  // Start streaming message with pending widget
  const { index: streamingIndex, id: streamingId } = helpers.streaming.startStreamingMessage({
    text: 'reading content...',
    html: getPendingWidgetHtml('reading content'),
    sourceTag: 'video_script_analysis_pending',
  });

  try {
    // Call Claude 4.5 Sonnet for Stage 1 analysis
    const result = await runVideoScriptAnalysis({
      sourceContent: sourceContent.content,
      sourceTitle: sourceContent.title,
      sourceType: sourceContent.source,
      onChunk: (chunk, accumulated) => {
        helpers.streaming.updateStreamingMessage(streamingIndex, {
          text: `reading content... (${accumulated.length} chars)`,
          html: getPendingWidgetHtml('reading content'),
        });
      },
    });

    // Record token usage
    if (result.tokenUsage) {
      workflows.recordTransaction({
        tokenUsage: result.tokenUsage,
        source: 'agenticWorkflows',
        modelOverride: buildModelDisplay(result.tokenUsage, 'Script Analysis', PERPLEXITY_VIDEO_SCRIPT_STAGE1_DISPLAY),
        summary: 'video script analysis',
      });
    }

    // Parse the Stage 1 response
    const parsed = parseVideoScriptStage1Response(result.text);
    if (!parsed) {
      throw new Error('failed to parse video script analysis response');
    }

    // Update session with Stage 1 analysis (use sync setter to prevent race condition with user reply)
    workflows.setVideoScriptSessionSync(prev => ({
      ...prev,
      stage: parsed.needsClarification ? 'stage1_clarify' : 'stage2_generating',
      stage1Analysis: parsed,
    }));

    if (parsed.needsClarification && parsed.clarifyingQuestions && parsed.clarifyingQuestions.length > 0) {
      // Finalize streaming message directly with clarifying questions (no intermediate message)
      helpers.streaming.finalizeStreamingMessage(streamingIndex, {
        text: 'please answer the following questions to optimize your video script:',
        html: formatVideoScriptClarifyingQuestionsHtml(parsed.summary.topic, parsed.clarifyingQuestions, GLOBAL_VIDEO_SCRIPT_BUTTON_IDS.proceed),
        sourceTag: 'video_script_clarify',
      });
    } else {
      // No clarification needed - proceed directly to Stage 2 without intermediate message
      // The "ready to generate" message is unnecessary visual clutter
      helpers.streaming.finalizeStreamingMessage(streamingIndex, {
        text: '',
        html: '',
        sourceTag: 'video_script_analysis_complete',
      });

      // Continue to Stage 2 automatically
      await handleVideoScriptStage2(helpers, workflows, []);
    }
  } catch (error) {
    console.error('video script analysis failed', error);
    const errorSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`;
    helpers.streaming.finalizeStreamingMessage(streamingIndex, {
      text: `unable to analyze content: ${formatVideoScriptError(error)}`,
      html: generateSpecialMessageTemplate({
        header: { title: 'unable to analyze content', iconHtml: errorSvg },
        sections: [
          { type: 'text', content: formatVideoScriptError(error), style: { fontSize: '0.9rem' } },
        ],
        theme: 'custom',
        customColors: RED_ERROR_THEME,
      }),
      sourceTag: 'video_script_error',
    });

    // Reset state on error
    workflows.setIsGeneratingVideoScript(false);
    workflows.resetVideoScriptSession();
  }

  return true;
}

/**
 * Handle "Write Video Script" button click from deep research Stage 1.
 * Reads Stage 1 findings directly from the drSessionRef to avoid data attribute limitations.
 * This allows users to generate video scripts from Stage 1 findings before full research completes.
 */
export async function handleWriteVideoScriptFromStage1(
  helpers: WorkflowHelpersInterface,
  workflows: AgenticWorkflowsHook
): Promise<boolean> {
  // Check if already generating
  if (workflows.isGeneratingVideoScript) {
    const hourglassSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 22h14"></path><path d="M5 2h14"></path><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"></path><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"></path></svg>`;
    helpers.addSystemMessage({
      text: 'video script generation is already in progress. please wait for it to complete.',
      html: generateSpecialMessageTemplate({
        header: { title: 'please wait', iconHtml: hourglassSvg },
        sections: [
          { type: 'text', content: 'video script generation is already in progress.', style: { fontSize: '0.9rem' } },
        ],
        theme: 'green',
      }),
      sourceTag: 'video_script_status',
    });
    return true;
  }

  // Read Stage 1 data from the deep research session ref
  const drSession = workflows.drSessionRef.current;

  // Validate we have Stage 1 data available
  if (!drSession || !drSession.stage1Findings) {
    const alertSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>`;
    helpers.addSystemMessage({
      text: 'no Stage 1 research data available.',
      html: generateSpecialMessageTemplate({
        header: { title: 'no content available', iconHtml: alertSvg },
        sections: [
          {
            type: 'text',
            content: 'Stage 1 research data is not available. Please start a new deep research query first, then click <strong>write video script</strong>.',
            style: { fontSize: '0.9rem', custom: 'line-height:1.5;' },
          },
        ],
        theme: 'green',
      }),
      sourceTag: 'video_script_status',
    });
    return true;
  }

  // Extract data from drSession
  const findings = drSession.stage1Findings;
  const attachments = drSession.attachmentContent || '';
  const query = drSession.originalQuery || 'Research Findings';

  // Combine findings and attachments
  const combinedContent = attachments
    ? `${findings}\n\n--- Document Analysis ---\n${attachments}`
    : findings;

  // Reset deep research session AFTER extracting data but BEFORE starting video script
  // This prevents GlobalTools from intercepting video script clarification messages
  // (GlobalTools processes messages FIRST, so if drSession is in 'stage1_confirm', it would
  // intercept user replies meant for video script stage1_clarify)
  workflows.resetDrSession();

  // Create source content for video script workflow
  const sourceContent: VideoScriptSourceContent = {
    content: combinedContent,
    title: query.slice(0, 100) || 'Research Findings',
    source: 'research',
    timestamp: new Date(),
  };

  // Set generating state
  workflows.setIsGeneratingVideoScript(true);

  // Initialize video script session (use sync setter to prevent race conditions)
  workflows.setVideoScriptSessionSync(() => ({
    stage: 'stage1_analyzing',
    sourceContent,
    stage1Analysis: null,
    userClarifications: [],
    stage2Output: null,
    startedAt: new Date(),
    completedAt: null,
  }));

  // Start streaming message with pending widget
  const { index: streamingIndex, id: streamingId } = helpers.streaming.startStreamingMessage({
    text: 'reading content...',
    html: getPendingWidgetHtml('reading content'),
    sourceTag: 'video_script_analysis_pending',
  });

  try {
    // Call Claude 4.5 Sonnet for Stage 1 analysis
    const result = await runVideoScriptAnalysis({
      sourceContent: sourceContent.content,
      sourceTitle: sourceContent.title,
      sourceType: sourceContent.source,
      onChunk: (chunk, accumulated) => {
        // Keep pending widget visible during analysis
        helpers.streaming.updateStreamingMessage(streamingIndex, {
          text: `analyzing... ${accumulated.length} chars`,
          html: getPendingWidgetHtml('analyzing'),
        });
      },
      onComplete: (fullText, tokenUsage) => {
        if (tokenUsage) {
          workflows.recordTransaction({
            tokenUsage: { ...tokenUsage, summary: 'video script analysis' },
            source: 'video_script_stage1',
            modelOverride: buildModelDisplay(tokenUsage, 'Script Analysis', PERPLEXITY_VIDEO_SCRIPT_STAGE1_DISPLAY),
            summary: 'video script analysis',
          });
        }
      },
    });

    // Parse the Stage 1 response
    const stage1Result = parseVideoScriptStage1Response(result.text);

    // Update session with Stage 1 result (use sync setter to prevent race condition with user reply)
    workflows.setVideoScriptSessionSync(prev => ({
      ...prev,
      stage: stage1Result.needsClarification ? 'stage1_clarify' : 'stage2_generating',
      stage1Analysis: stage1Result,
    }));

    if (stage1Result.needsClarification) {
      // Finalize streaming message directly with clarifying questions (no intermediate message)
      const questionsHtml = formatVideoScriptClarifyingQuestionsHtml(
        stage1Result.summary.topic,
        stage1Result.clarifyingQuestions,
        GLOBAL_VIDEO_SCRIPT_BUTTON_IDS.proceed
      );

      helpers.streaming.finalizeStreamingMessage(streamingIndex, {
        text: 'please answer the following questions to optimize your video script.',
        html: questionsHtml,
        sourceTag: 'video_script_questions',
        tokenUsage: result.tokenUsage ? { ...result.tokenUsage, summary: 'video script analysis' } : undefined,
      });
    } else {
      // No clarification needed - proceed directly to Stage 2 without intermediate message
      // The "ready to generate" message is unnecessary visual clutter
      helpers.streaming.finalizeStreamingMessage(streamingIndex, {
        text: '',
        html: '',
        sourceTag: 'video_script_analysis_complete',
      });

      await handleVideoScriptStage2(helpers, workflows, []);
    }
  } catch (error) {
    console.error('video script analysis failed', error);
    const errorSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`;
    helpers.streaming.finalizeStreamingMessage(streamingIndex, {
      text: `unable to analyze content: ${formatVideoScriptError(error)}`,
      html: generateSpecialMessageTemplate({
        header: { title: 'unable to analyze content', iconHtml: errorSvg },
        sections: [
          { type: 'text', content: formatVideoScriptError(error), style: { fontSize: '0.9rem' } },
        ],
        theme: 'custom',
        customColors: RED_ERROR_THEME,
      }),
      sourceTag: 'video_script_error',
    });

    workflows.setIsGeneratingVideoScript(false);
    workflows.resetVideoScriptSession();
  }

  return true;
}

/**
 * Handle Stage 2 video script generation.
 * Called either directly when no clarification is needed, or after user provides clarifications.
 */
export async function handleVideoScriptStage2(
  helpers: WorkflowHelpersInterface,
  workflows: AgenticWorkflowsHook,
  userClarifications: VideoScriptClarification[]
): Promise<boolean> {
  const session = workflows.videoScriptSessionRef?.current || workflows.videoScriptSession;

  if (!session || !session.sourceContent || !session.stage1Analysis) {
    const errorSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`;
    helpers.addSystemMessage({
      text: 'video script session state is missing. please try again.',
      html: generateSpecialMessageTemplate({
        header: { title: 'session state missing', iconHtml: errorSvg },
        sections: [
          { type: 'text', content: 'Video script session state is missing. Please click <strong>write video script</strong> again to restart.', style: { fontSize: '0.9rem' } },
        ],
        theme: 'custom',
        customColors: RED_ERROR_THEME,
      }),
      sourceTag: 'video_script_error',
    });
    workflows.setIsGeneratingVideoScript(false);
    workflows.resetVideoScriptSession();
    return true;
  }

  // Validate and provide defaults for stage1Summary fields
  const summary = session.stage1Analysis.summary;
  const validatedSummary = {
    topic: summary.topic || session.sourceContent.title || 'Immigration Law Topic',
    targetAudience: summary.targetAudience || 'General audience seeking immigration information',
    emotionalState: summary.emotionalState || 'Seeking clarity and guidance',
    platformAndLength: summary.platformAndLength || 'YouTube, 2-3 minutes',
    primaryGoalCta: summary.primaryGoalCta || 'Schedule a consultation to discuss your specific situation',
  };

  // Update session with validated summary if any fields were missing
  if (validatedSummary.topic !== summary.topic ||
      validatedSummary.targetAudience !== summary.targetAudience ||
      validatedSummary.emotionalState !== summary.emotionalState ||
      validatedSummary.platformAndLength !== summary.platformAndLength ||
      validatedSummary.primaryGoalCta !== summary.primaryGoalCta) {
    workflows.setVideoScriptSession(prev => ({
      ...prev,
      stage1Analysis: prev.stage1Analysis ? {
        ...prev.stage1Analysis,
        summary: validatedSummary,
      } : null,
    }));
  }

  // Update session state (use sync setter to prevent race conditions)
  workflows.setVideoScriptSessionSync(prev => ({
    ...prev,
    stage: 'stage2_generating',
    userClarifications,
  }));

  // Start streaming message with pending widget (uses overlay pattern - not in message box)
  const { index: streamingIndex, id: streamingId } = helpers.streaming.startStreamingMessage({
    text: 'writing script...',
    html: getPendingWidgetHtml('writing script'),
    sourceTag: 'video_script_generation_pending',
  });

  // Allow React to render
  await new Promise((resolve) => setTimeout(resolve, 0));

  try {
    let lastUpdateTime = 0;
    const UPDATE_THROTTLE_MS = 500;

    // Call Claude 4.5 Sonnet for Stage 2 generation
    const result = await runVideoScriptGeneration({
      sourceContent: session.sourceContent.content,
      sourceTitle: session.sourceContent.title,
      stage1Summary: validatedSummary,
      userClarifications,
      onChunk: (_chunk, accumulated) => {
        const now = Date.now();
        if (now - lastUpdateTime >= UPDATE_THROTTLE_MS) {
          lastUpdateTime = now;
          helpers.streaming.updateStreamingMessage(streamingIndex, {
            text: `generating script... ${accumulated.length} chars`,
          });
        }
      },
      onComplete: (fullText, tokenUsage) => {
        // Record token usage for Stage 2
        if (tokenUsage) {
          workflows.recordTransaction({
            tokenUsage: { ...tokenUsage, summary: 'video script generation' },
            source: 'video_script_stage2',
            modelOverride: buildModelDisplay(tokenUsage, 'Script Generation', CLAUDE_VIDEO_SCRIPT_GENERATION_DISPLAY),
            summary: 'video script generation',
          });
        }
      },
    });

    // Parse the Stage 2 response
    const parsedScript = parseVideoScriptStage2Response(result.text);

    // VALIDATION: Fail if the parsed script is empty (model returned malformed/empty output)
    if (!parsedScript.script || parsedScript.script.trim().length === 0) {
      console.error('[VideoScript Legacy] Parsed script is empty - model returned no usable content');
      console.error('[VideoScript Legacy] Raw response length:', result.text?.length || 0);
      const errorSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`;
      helpers.streaming.finalizeStreamingMessage(streamingIndex, {
        text: 'Video script generation returned empty content. The model may have timed out or produced an unparseable response. Please try again.',
        html: generateSpecialMessageTemplate({
          header: { title: 'unable to generate script', iconHtml: errorSvg },
          sections: [
            { type: 'text', content: 'The model returned an empty or unparseable script. This can happen if the browser tab was dormant or the request timed out. Please try again.', style: { fontSize: '0.9rem' } },
          ],
          theme: 'custom',
          customColors: RED_ERROR_THEME,
        }),
        sourceTag: 'video_script_error',
      });
      workflows.setVideoScriptSession(prev => ({
        ...prev,
        stage: 'idle',
      }));
      return true;
    }

    // Build the full VideoScriptStage2Result structure
    const stage2Output = {
      reasoning: {
        audiencePsychology: '',
        hookStrategy: parsedScript.productionNotes.hookStrategy || '',
        contentStrategy: '',
        ctaStrategy: '',
      },
      script: {
        title: parsedScript.title,
        fullText: parsedScript.script,
        sections: [],
        disclaimer: '',
      },
      metadata: {
        estimatedLengthSeconds: parseInt(parsedScript.estimatedLength) * 60 || 120,
        estimatedLengthFormatted: parsedScript.estimatedLength,
        characterCount: parsedScript.characterCount,
        wordCount: parsedScript.wordCount,
        hookType: '',
        tone: parsedScript.productionNotes.recommendedTone || '',
        emphasisPoints: parsedScript.productionNotes.emphasisPoints,
      },
      alternativeHooks: [],
      shortFormVersion: null,
      rawResponse: result.text,
    };

    // Update session with Stage 2 result
    workflows.setVideoScriptSession(prev => ({
      ...prev,
      stage: 'complete',
      stage2Output,
      completedAt: new Date(),
    }));

    // Format the output HTML with translate button
    const outputHtml = formatVideoScriptOutputHtml(
      parsedScript,
      `${CHAT_VIDEO_SCRIPT_TRANSLATE_BUTTON_ID}-output`
    );

    // Set translatable content for Spanish translation workflow
    const translatableData = {
      content: parsedScript.script,
      title: parsedScript.title,
      timestamp: new Date(),
      source: 'video_script' as const,
    };
    workflows.setLastTranslatableContent(translatableData);
    workflows.lastTranslatableContentRef.current = translatableData;

    // Build the video script PDF — MT-QA-BUG-001: thread tenant firm
    // name into PDF metadata/footer so T1/T2 video scripts don't inherit
    // Fernando branding.
    const videoTenantBranding = await fetchCurrentTenantBranding();
    const pdf = await buildVideoScriptPdf({
      title: parsedScript.title,
      estimatedLength: parsedScript.estimatedLength,
      script: parsedScript.script,
      productionNotes: parsedScript.productionNotes,
      characterCount: parsedScript.characterCount,
      wordCount: parsedScript.wordCount,
      createdAt: new Date(),
      translations: { organizationName: videoTenantBranding.firmName },
    });

    // Serialize the PDF for persistence (localStorage fallback)
    const pdfDataUrl = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve('');
      reader.readAsDataURL(pdf);
    });

    const pdfSerialized: SerializedAttachment = {
      name: pdf.name,
      type: pdf.type,
      size: pdf.size,
      lastModified: pdf.lastModified,
      dataUrl: pdfDataUrl,
    };

    // Upload to Firebase Storage for cross-device persistence
    const auth = getAuth();
    const currentUid = auth.currentUser?.uid;
    let finalPdfSerialized: SerializedAttachment = pdfSerialized;

    if (currentUid) {
      try {
        const pdfWithStorage = await serializeAttachmentWithStorage(pdf, currentUid, `vs-${Date.now()}`);
        if (pdfWithStorage.storageUrl) {
          finalPdfSerialized = pdfWithStorage;
          console.log('[VideoScript] Storage upload successful - PDF will persist cross-device');
        } else {
          console.warn('[VideoScript] Storage upload returned no URL - using localStorage fallback');
        }
      } catch (err) {
        console.warn('[VideoScript] Storage upload failed - using localStorage fallback:', err);
      }
    }

    // Finalize streaming message with the script and PDF attachment
    helpers.streaming.finalizeStreamingMessage(streamingIndex, {
      text: parsedScript.script,
      html: outputHtml,
      sourceTag: 'video_script_result',
      attachments: [pdf],
      attachmentsSerialized: [finalPdfSerialized],
      tokenUsage: result.tokenUsage ? { ...result.tokenUsage, summary: 'video script generation' } : undefined,
    });

  } catch (error) {
    console.error('video script generation failed', error);
    const errorSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`;
    helpers.streaming.finalizeStreamingMessage(streamingIndex, {
      text: `unable to generate video script: ${formatVideoScriptError(error)}`,
      html: generateSpecialMessageTemplate({
        header: { title: 'unable to generate script', iconHtml: errorSvg },
        sections: [
          { type: 'text', content: formatVideoScriptError(error), style: { fontSize: '0.9rem' } },
        ],
        theme: 'custom',
        customColors: RED_ERROR_THEME,
      }),
      sourceTag: 'video_script_error',
    });
  } finally {
    // Always reset generating state when done
    workflows.setIsGeneratingVideoScript(false);
  }

  return true;
}

/**
 * Handle "Continue" button click after user provides clarification answers.
 * Parses the user's answers from the chat and proceeds to Stage 2.
 */
export async function handleVideoScriptContinue(
  helpers: WorkflowHelpersInterface,
  workflows: AgenticWorkflowsHook,
  userAnswers: string
): Promise<boolean> {
  const session = workflows.videoScriptSessionRef?.current || workflows.videoScriptSession;

  if (!session || session.stage !== 'stage1_clarify' || !session.stage1Analysis) {
    const errorSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`;
    helpers.addSystemMessage({
      text: 'no pending video script clarification to continue.',
      html: generateSpecialMessageTemplate({
        header: { title: 'no pending clarification', iconHtml: errorSvg },
        sections: [
          { type: 'text', content: 'No pending video script clarification. Please start a new video script request.', style: { fontSize: '0.9rem' } },
        ],
        theme: 'custom',
        customColors: RED_ERROR_THEME,
      }),
      sourceTag: 'video_script_error',
    });
    return true;
  }

  // Parse user answers and match them to questions
  const questions = session.stage1Analysis.clarifyingQuestions;
  const clarifications: VideoScriptClarification[] = [];

  // Split by newlines and try to match answers
  const answerLines = userAnswers.split(/\n+/).filter(line => line.trim());

  for (let i = 0; i < questions.length; i++) {
    const answer = answerLines[i]?.trim() || '';
    if (answer) {
      clarifications.push({
        question: questions[i].question,
        answer,
        timestamp: new Date(),
      });
    }
  }

  // If no answers provided, use the first option as a default
  if (clarifications.length === 0) {
    for (const q of questions) {
      if (q.options && q.options.length > 0) {
        clarifications.push({
          question: q.question,
          answer: q.options[0],
          timestamp: new Date(),
        });
      }
    }
  }

  // Proceed to Stage 2 with clarifications
  return await handleVideoScriptStage2(helpers, workflows, clarifications);
}

/**
 * Handle "Skip" button click to bypass clarification questions.
 * Proceeds directly to Stage 2 without additional context.
 */
export async function handleVideoScriptSkip(
  helpers: WorkflowHelpersInterface,
  workflows: AgenticWorkflowsHook
): Promise<boolean> {
  const session = workflows.videoScriptSessionRef?.current || workflows.videoScriptSession;

  if (!session || session.stage !== 'stage1_clarify' || !session.stage1Analysis) {
    const errorSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`;
    helpers.addSystemMessage({
      text: 'no pending video script clarification to skip.',
      html: generateSpecialMessageTemplate({
        header: { title: 'no pending clarification', iconHtml: errorSvg },
        sections: [
          { type: 'text', content: 'No pending video script clarification. Please start a new video script request.', style: { fontSize: '0.9rem' } },
        ],
        theme: 'custom',
        customColors: RED_ERROR_THEME,
      }),
      sourceTag: 'video_script_error',
    });
    return true;
  }

  // Proceed to Stage 2 without clarifications (writing script spinner will show)
  return await handleVideoScriptStage2(helpers, workflows, []);
}

/**
 * Handle "Write Video Script" button click from clarifying questions.
 * Proceeds directly to Stage 2 without additional user context.
 * Uses fallback strategies to handle edge cases where session state is corrupted.
 */
export async function handleVideoScriptProceed(
  helpers: WorkflowHelpersInterface,
  workflows: AgenticWorkflowsHook
): Promise<boolean> {
  const videoSession = workflows.videoScriptSessionRef?.current || workflows.videoScriptSession;
  const drSession = workflows.drSessionRef?.current;

  // PRIMARY: In stage1_clarify with valid analysis → proceed to Stage 2
  if (videoSession?.stage === 'stage1_clarify' && videoSession?.stage1Analysis) {
    return await handleVideoScriptStage2(helpers, workflows, []);
  }

  // FALLBACK A: Has stage1Analysis but wrong stage → try to proceed anyway
  // This handles cases where session state was corrupted but analysis data exists
  if (videoSession?.stage1Analysis) {
    console.warn('handleVideoScriptProceed: proceeding despite stage mismatch:', videoSession.stage);
    // Update stage to stage2_generating before proceeding
    workflows.setVideoScriptSessionSync(prev => ({
      ...prev,
      stage: 'stage2_generating',
    }));
    return await handleVideoScriptStage2(helpers, workflows, []);
  }

  // FALLBACK B: Deep research has Stage 1 findings → use those to start video script workflow
  if (drSession?.stage1Findings && drSession.stage1Findings.trim().length > 2) {
    console.log('handleVideoScriptProceed: falling back to deep research Stage 1 findings');
    return await handleWriteVideoScriptFromStage1(helpers, workflows);
  }

  // LAST RESORT: No viable path forward → show error
  console.warn('handleVideoScriptProceed: no viable path forward');
  const errorSvg = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`;
  helpers.addSystemMessage({
    text: 'unable to proceed - no video script data available.',
    html: generateSpecialMessageTemplate({
      header: { title: 'unable to proceed', iconHtml: errorSvg },
      sections: [
        { type: 'text', content: 'no video script analysis data is available. please start fresh by uploading a document or typing "write a video script [topic]".', style: { fontSize: '0.9rem' } },
      ],
      theme: 'custom',
      customColors: RED_ERROR_THEME,
    }),
    sourceTag: 'video_script_error',
  });
  workflows.resetVideoScriptSession();
  return true;
}

// ============================================================================
// Main Handler Factory
// ============================================================================

/**
 * Create the main agentic workflow message handler.
 * This handler intercepts messages when agentic workflows are enabled.
 */
export function createAgenticWorkflowHandler(workflows: AgenticWorkflowsHook) {
  return async (
    message: ChatMessage,
    helpers: WorkflowHelpersInterface
  ): Promise<boolean> => {
    if (!workflows.isAuthorized) {
      return false;
    }

    return await handleDeepResearch(message, helpers, workflows);
  };
}

/**
 * Create chat action event handler for button clicks.
 */
export function createChatActionHandler(workflows: AgenticWorkflowsHook) {
  return (action: string, helpers: WorkflowHelpersInterface): void => {
    if (!workflows.isAuthorized) return;

    if (action === 'translate' || action === 'translate-spanish') {
      // Both 'translate' and legacy 'translate-spanish' actions trigger the same handler
      handleTranslateToSpanish(helpers, workflows);
    } else if (action === 'write-video-script') {
      handleWriteVideoScript(helpers, workflows);
    }
  };
}
